import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  useToast,
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Stack,
  IconButton,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, UseFormRegister, UseFormReturn, UseFormSetValue, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import {
  FileCollection,
  validateMaxFile,
  validateTypeFile,
  validationSizeFile,
} from '../../../../../utils/validationForm';
import { useNewExemption } from '../../../../../services/hooks/Exemptions/UseNewExemption';
import MessageError from '../../../../../components/MessageError';
import { IoChevronDownCircleOutline, IoDocumentAttachOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { FiEye, FiFile, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { formatFileSize } from '../../../../../utils/functions';

interface IOwnProps {
  exemptionId: string;
  cpf?: string;
  birth_date?: string;
  registrationId: string;
  hideButton?: boolean;
  hookForms?: UseFormReturn<INISProps>;
  onSetValue?: UseFormSetValue<FieldValues>;
  nis?: string;
  exemptionName?: string;
  nisRequired?: boolean;
  documentRequired?: boolean;
  requirementsLoaded?: boolean;
}

export interface INISProps {
  nis?: string;
  attachmentExemption?: FileCollection;
}

interface ISelectedFileRowProps {
  file: File;
  onRemove: () => void;
}

const SelectedFileRow: React.FC<ISelectedFileRowProps> = ({ file, onRemove }) => {
  const [fileUrl, setFileUrl] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setFileUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <Flex
      p={3}
      border="1px solid"
      borderColor="green.200"
      borderRadius="lg"
      bg="green.50"
      boxShadow="sm"
      minW={0}
      align="center"
      gap={3}
    >
      <Icon as={FiFile} boxSize={5} color="green.600" flexShrink={0} />
      <Stack spacing={0} minW={0} flex={1}>
        <Text fontSize="sm" fontWeight="semibold" isTruncated title={file.name}>
          {file.name}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {formatFileSize(file.size)}
        </Text>
      </Stack>
      <HStack spacing={1} flexShrink={0}>
        <IconButton
          type="button"
          aria-label={`Visualizar ${file.name}`}
          title="Visualizar arquivo"
          icon={<FiEye />}
          variant="ghost"
          size="sm"
          colorScheme="blue"
          isDisabled={!fileUrl}
          onClick={() => {
            if (fileUrl) window.open(fileUrl, '_blank', 'noopener,noreferrer');
          }}
        />
        <IconButton
          type="button"
          aria-label={`Remover ${file.name}`}
          title="Remover arquivo"
          icon={<FiTrash2 />}
          variant="ghost"
          size="sm"
          colorScheme="red"
          onClick={onRemove}
        />
      </HStack>
    </Flex>
  );
};

const NIS: React.FC<IOwnProps> = ({
  exemptionId,
  exemptionName = 'CadÚnico',
  nisRequired = true,
  documentRequired = true,
  requirementsLoaded = true,
  nis,
  registrationId,
  hideButton = false,
  hookForms,
  onSetValue,
}) => {
  const newExemption = useNewExemption();
  const toast = useToast();
  const navigate = useNavigate();

  const schema = Yup.object().shape({
    nis: Yup.string()
      .test('nis-required', 'NIS é obrigatório.', (value) => !nisRequired || Boolean(value?.trim()))
      .test('nis-format', 'O NIS deve conter 11 dígitos numéricos.', (value) => !nisRequired || /^\d{11}$/.test(value || '')),
    attachmentExemption: Yup.mixed<FileCollection>()
      .test(
        'required',
        'Anexe o comprovante de cadastro e regularidade do CadÚnico.',
        (file) => !documentRequired || Boolean(file?.length),
      )
      .test('validationSizeFile', 'Tamanho máximo para arquivos é de 2MB', (file) => {
        const listFile = file as FileCollection;
        if (listFile?.length > 0) {
          return validationSizeFile(listFile);
        }
        return true;
      })
      .test('validationMaxFile', 'Quantidade máxima de arquivos é de 5 arquivos', (file) => {
        const listFile = file as FileCollection;
        if (listFile?.length > 0) {
          return validateMaxFile(listFile);
        }
        return true;
      })
      .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
        const listFile = file as FileCollection;
        if (listFile?.length > 0) {
          return validateTypeFile(listFile);
        }
        return true;
      }),
  });

  const { register, handleSubmit, formState, watch, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  const initialFiles = (hookForms?.getValues('attachmentExemption') ?? watch('attachmentExemption')) as
    | FileCollection
    | undefined;
  const [selectedFiles, setSelectedFiles] = useState<File[]>(() => Array.from(initialFiles || []));
  const hasFiles = selectedFiles.length > 0;
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);

  const updateSelectedFiles = (files: File[]) => {
    setSelectedFiles(files);

    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }

    const updateOptions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
    if (hookForms) {
      hookForms.setValue('attachmentExemption', files, updateOptions);
      void hookForms.trigger('attachmentExemption');
    } else {
      setValue('attachmentExemption', files, updateOptions);
    }
  };

  const addSelectedFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.currentTarget.files?.[0];
    if (!selectedFile) return;

    updateSelectedFiles([...selectedFiles, selectedFile]);
  };

  const removeSelectedFile = (fileIndex: number) => {
    updateSelectedFiles(selectedFiles.filter((_, index) => index !== fileIndex));
  };

  const handleCreateNewExemption = (data: FieldValues) => {
    exemptionSolicitation(data);
  };

  const handleGoToCandidateArea = (data: FieldValues) => {
    exemptionSolicitation(data, '/painel/isencoes');
  };

  const exemptionSolicitation = (data: FieldValues, route?: string) => {
    newExemption
      .mutateAsync({
        ...data,
        type: exemptionId,
        registration: registrationId,
        file_roles: Array.from(data.attachmentExemption ?? []).map(() => exemptionName),
      })
      .then(() => {
        toast({
          title: 'Sucesso',
          description: 'Pedido de isenção enviado com sucesso',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        if (onSetValue) onSetValue('type_exemption', '');
        if (route) {
          navigate(route);
        }
      })
      .catch(() => {
        toast({
          title: 'Erro',
          description: 'Erro na solicitação, tente novamente',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const currentFormState = hookForms ? hookForms.formState : formState;
  const currentRegister = (hookForms ? hookForms.register : register) as UseFormRegister<FieldValues>;
  const hasAttachmentError = currentFormState.errors.attachmentExemption?.message;

  return (
    <Stack p={0} m={0} width="100%">
      <Flex gap={8} align="flex-start">
        <VStack spacing={4} flex="1" align="stretch" w={'100%'}>
          <FormControl isRequired={nisRequired} isInvalid={Boolean(currentFormState.errors.nis)}>
            <HStack spacing={3} mb={2}>
              <FormLabel fontSize="14px" fontWeight="semibold" mb={0}>
                NIS:
              </FormLabel>
            </HStack>
            <Input
              {...currentRegister('nis')}
              defaultValue={nis}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              type="text"
              inputMode="numeric"
              maxLength={11}
              _hover={{ borderColor: 'gray.300' }}
              _focus={{ borderColor: 'gray.300', boxShadow: 'none' }}
              fontSize="sm"
            />
            <MessageError>{currentFormState.errors.nis?.message?.toString()}</MessageError>
          </FormControl>

          {/* <FormControl>
            <HStack spacing={3} mb={2}>
              <Icon as={IoPersonOutline} color="gray.500" />
              <FormLabel fontSize="sm" fontWeight="500" color="gray.700" mb={0}>
                CPF
              </FormLabel>
            </HStack>
            <Input
              value={cpf}
              readOnly
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              _hover={{ borderColor: 'gray.300' }}
              _focus={{ borderColor: 'gray.300', boxShadow: 'none' }}
              cursor="not-allowed"
              fontSize="sm"
            />
          </FormControl>

          <FormControl isRequired={documentRequired} isInvalid={Boolean(hasAttachmentError)}>
            <HStack spacing={3} mb={2}>
              <Icon as={IoCalendarOutline} color="gray.500" />
              <FormLabel fontSize="sm" fontWeight="500" color="gray.700" mb={0}>
                Data de Nascimento
              </FormLabel>
            </HStack>
            <Input
              value={birth_date}
              readOnly
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              _hover={{ borderColor: 'gray.300' }}
              _focus={{ borderColor: 'gray.300', boxShadow: 'none' }}
              cursor="not-allowed"
              fontSize="sm"
            />
          </FormControl> */}

          <FormControl>
            <HStack justify="space-between" mb={3}>
              <FormLabel fontSize="14px" fontWeight="semibold">
                Anexe o comprovante de cadastro e regularidade do CadÚnico:
              </FormLabel>
              {hasFiles && (
                <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2}>
                  <Icon as={IoCheckmarkCircleOutline} mr={1} />
                  {selectedFiles.length} arquivo(s)
                </Badge>
              )}
            </HStack>

            <Box
              position="relative"
              border="2px dashed"
              borderColor={hasAttachmentError && !hasFiles ? 'red.300' : hasFiles ? 'green.300' : 'gray.300'}
              borderRadius="8px"
              height="124px;"
              p={6}
              bg={hasFiles ? 'green.50' : 'gray.50'}
              transition="all 0.2s"
              _hover={{
                borderColor: hasAttachmentError && !hasFiles ? 'red.400' : hasFiles ? 'green.400' : 'blue.300',
                bg: hasFiles ? 'green.100' : 'blue.50',
              }}
            >
              <Input
                ref={attachmentInputRef}
                onChange={addSelectedFile}
                type="file"
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                opacity="0"
                cursor="pointer"
                accept=".pdf,.jpg,.jpeg,.png"
              />

              <VStack spacing={3}>
                <Icon
                  as={hasFiles ? IoCheckmarkCircleOutline : IoDocumentAttachOutline}
                  size="32px"
                  color={hasFiles ? 'green.500' : 'gray.400'}
                />

                <Text
                  fontSize="sm"
                  color={hasFiles ? 'green.700' : 'gray.600'}
                  textAlign="center"
                  fontWeight={hasFiles ? '600' : 'normal'}
                >
                  {hasFiles
                    ? `✓ ${selectedFiles.length} arquivo(s) selecionado(s) · clique para adicionar outro`
                    : 'Clique aqui ou arraste um arquivo'}
                </Text>

                {!hasFiles && (
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    Formatos: PDF, JPG ou PNG (máx. 2MB cada)
                  </Text>
                )}
              </VStack>
            </Box>

            {hasFiles && (
              <Stack spacing={2} mt={2}>
                {selectedFiles.map((file, index) => (
                  <SelectedFileRow
                    key={`${file.name}-${file.lastModified}-${index}`}
                    file={file}
                    onRemove={() => removeSelectedFile(index)}
                  />
                ))}
              </Stack>
            )}
          </FormControl>

          {/* Mensagens de Erro */}
          {hasAttachmentError && <MessageError>{hasAttachmentError}</MessageError>}
        </VStack>

        <VStack spacing={4} flex="1" align="stretch" minW="300px"></VStack>
      </Flex>

      {/* Botões de Ação */}
      {!hideButton && (
        <Flex justify="flex-end" mt={8} pt={6} borderTop="1px solid" borderColor="gray.100">
          <Menu>
            <MenuButton
              as={Button}
              isDisabled={newExemption.isPending || !requirementsLoaded}
              bg="blue.600"
              color="white"
              borderRadius="8px"
              px={6}
              h="44px"
              fontWeight="500"
              rightIcon={<IoChevronDownCircleOutline />}
              _hover={{ bg: 'blue.700' }}
              _active={{ bg: 'blue.800' }}
              _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            >
              {newExemption.isPending ? <Spinner size="sm" /> : 'Enviar Solicitação'}
            </MenuButton>
            <MenuList borderRadius="8px" border="1px solid" borderColor="gray.200" shadow="lg">
              <MenuItem onClick={handleSubmit(handleCreateNewExemption)} _hover={{ bg: 'gray.50' }} fontSize="sm">
                Enviar e permanecer na página
              </MenuItem>
              <MenuItem onClick={handleSubmit(handleGoToCandidateArea)} _hover={{ bg: 'gray.50' }} fontSize="sm">
                Enviar e ir para Isenções
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      )}
    </Stack>
  );
};

export default NIS;
